import { ControlEventTypes } from "Core/Events/EventTypes";
import { DropDownService, TextBlockService } from "Service";
import { IDataRow, IODataResult } from "Entities";
import { Navigation } from "Core";
import { BaseDropDownControl } from "Controls/BaseDropDownControl";
import { ControlDefinition, DataRequestOption, Environment } from "Service/Client";

/**
 * Textbox which shows suggestions as dropdown list.
 * The list is searchable with text input.
 * @since 4.1.0
 * @example
 * ```JavaScript
 * // Access the instance of a AutoComplete with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class AutoComplete extends BaseDropDownControl {
    /**
     * The css class which should be unique for each control type.
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-autocomplete";

    private _kendoElement: kendo.ui.AutoComplete;

    private data: IODataResult;

    private dataSourceOptions: kendo.data.DataSourceOptions;

    private comboboxService: DropDownService;

    /**
     * Create a new AutoCompleteControl.
     * @hidden
     */
    constructor(
        controlDefinition: ControlDefinition,
        textBlockCategory: string,
        textBlockService: TextBlockService,
        environment: Environment,
        comboBoxService: DropDownService
    ) {
        super(controlDefinition, textBlockCategory, textBlockService, environment);
        this.comboboxService = comboBoxService;
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));

        let dropDownOptions = this.getDropDownOptions();

        let options: kendo.ui.AutoCompleteOptions = {
            dataTextField: this.controlDefinition.CommandValueColumnName || this.controlDefinition.CommandTextColumnName,
            valuePrimitive: true, // sets the binding to the value field instead of the dataitem itself
            suggest: true,
            filter: "contains",
            change: (e) => this.onChange(e),
            // select: (e) => this.onSelect(e),
            dataBound: (e) => this.onDataBound(e),
            autoWidth: this.controlDefinition.AutoWidth || false,
            enforceMinLength: false,
            minLength: 0,
            clearButton: false,
            template: dropDownOptions.template,
            headerTemplate: dropDownOptions.headerTemplate,
            separator: this.controlDefinition.Mask || undefined,
            noDataTemplate: false
        };

        if (this.controlDefinition.LoadDataAsynchronously) {
            this.dataSourceOptions = {
                transport: {
                    read: (options: kendo.data.DataSourceTransportReadOptions) => {
                        // check if loaded data contains the requested page
                        if (this.data && this.data.value) {
                            let value = this.data.value;
                            if (value[options.data.skip]) {
                                let data = value[options.data.skip];
                                delete value[options.data.skip];

                                options.success({
                                    value: data,
                                    "odata.count": this.data["odata.count"],
                                });
                                return;
                            }
                        }

                        let dataRow = this.execute<IDataRow, {}>(ControlEventTypes.GetDataRow);
                        let requestOptions = this.getDataRequest();
                        requestOptions.SearchValue = null;
                        if (options.data.filter && options.data.filter.filters.length > 0) {
                            let item = <kendo.data.DataSourceFilterItem>options.data.filter.filters[0];
                            requestOptions.SearchText = item.value;
                        }
                        requestOptions.Skip = options.data.skip;

                        this.comboboxService
                            .get(this.controlDefinition.GridID, this.controlDefinition.ControlID, requestOptions, dataRow)
                            .then((result: IODataResult) => {
                                if (result.error) {
                                    Navigation.errorAsync(result.error, "Error");
                                    return;
                                }

                                options.success({
                                    value: result.value[0],
                                    "odata.count": result["odata.count"],
                                });
                            });
                    },
                },
                schema: {
                    data: function (data: any) {
                        return data["value"];
                    },
                    total: function (data: any) {
                        return data["odata.count"];
                    },
                },
                error: (e) => this.onError(e),
                serverPaging: true,
                serverFiltering: true,
                pageSize: this.controlDefinition.ItemsPerRequest > 0 ? this.controlDefinition.ItemsPerRequest : 10,
            };

            let asyncOptions: kendo.ui.AutoCompleteOptions = {
                height: ((this.controlDefinition.ItemsPerRequest > 0 ? this.controlDefinition.ItemsPerRequest : 10) / 4) * 30,
                virtual: {
                    itemHeight: 30,
                    // valueMapper: (options: any) => {
                    //     if (this.valueIndex !== undefined) {
                    //         options.success(this.valueIndex == -1 ? 0 : this.valueIndex);
                    //         this.valueIndex = undefined;
                    //         return;
                    //     }

                    //     if (!options.value) {
                    //         options.success(0); // try to select the first value
                    //         return;
                    //     }

                    //     this.data = null;
                    //     let dataRow = this.execute<IDataRow, {}>(ControlEventTypes.GetDataRow);
                    //     let requestOptions: IDataRequestOption = {
                    //         ControlID: this.controlDefinition.ControlID,
                    //         SearchValue: options.value != "" ? [options.value] : []
                    //     };
                    //     this.comboboxService.mapValue(this.controlDefinition.GridID, this.controlDefinition.ControlID, requestOptions, dataRow).then((result) => {
                    //         if (result.error) {
                    //             Navigation.errorAsync(result.error, "Error");
                    //             return;
                    //         }

                    //         options.success(result.selectedIndex.length > 0 ? result.selectedIndex[0] : 0);
                    //         if (result.selectedIndex.length == 0) {
                    //             this._kendoElement.value(null);
                    //             this._kendoElement.trigger("change");
                    //         }
                    //     });
                    // }
                },
            };

            $.extend(options, asyncOptions);
        } else {
            this.dataSourceOptions = {
                transport: {
                    read: (options: kendo.data.DataSourceTransportRead) => {
                        if (this.data != null) {
                            (options.success as JQuery.Ajax.SuccessCallback<any>)(this.data, null, null);
                            return;
                        }
                    },
                },
                schema: {
                    data: function (data: IODataResult) {
                        return data.value[0];
                    },
                    total: function (data: IODataResult) {
                        return data["odata.count"];
                    },
                },
                error: (e) => this.onError(e),
            };
        }

        this._kendoElement = new kendo.ui.AutoComplete(this.jQueryElement[0], options);
        this._kendoElement.popup.element.addClass("dms-control-dropdown-popup");

        if (!this.controlDefinition.LoadDataAsynchronously) {
            (<any>this._kendoElement)._filterSource = (s: any) => {
                if (!s) {
                    this._kendoElement.dataSource.filter({});
                } else {
                    let filter: kendo.data.DataSourceFilters = {};
                    filter.logic = "or";
                    filter.filters = [];

                    if (this.controlDefinition.DataColumns && this.controlDefinition.DataColumns.length > 0) {
                        for (let i = 0, len = this.controlDefinition.DataColumns.length; i < len; i++) {
                            let dataColumn = this.controlDefinition.DataColumns[i];
                            filter.filters.push({
                                field: dataColumn.ColumnName,
                                operator: "contains",
                                value: s.value,
                            });
                        }
                    } else {
                        filter.filters.push({
                            field: this._kendoElement.options.dataTextField,
                            operator: "contains",
                            value: s.value,
                        });
                    }
                    this._kendoElement.dataSource.filter(filter);
                }
            };
        }

        if (dropDownOptions.setListWidth) {
            let listWidth = dropDownOptions.listWidth;
            if (listWidth > this._kendoElement.element.width()) {
                this._kendoElement.list.width(listWidth);
            }
        }
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.1.0
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Get the display template for grid cells.
     * @param bindingField Name of field.
     * @hidden
     */
    public getDisplayTemplate(bindingField?: string): string {
        return `#= get("${this.controlDefinition.ValueColumnName}") == null ? "${this.controlDefinition.ReplaceNullValue || ""}" : get("${
            this.controlDefinition.ValueColumnName
        }") #`;
    }

    /**
     * Format the value with highlighting from text search.
     * @param value Value to be formatted.
     * @hidden
     */
    public formatValue(value: string): string {
        let text = this._kendoElement.value();
        // let idx = this._kendoElement.select();
        if (!text || !value /*|| idx == -1*/) {
            return value;
        }
        if (this.lastText != text) {
            // todo: refactor replace to own service.
            this.textMatcher = new RegExp(text.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"), "ig");
            this.lastText = text;
        }
        return value.toString().replace(this.textMatcher, function (match) {
            return "<em>" + match + "</em>";
        });
    }

    /**
     * Occurs after data was bound on the control.
     * @param e EventArgs.
     */
    private onDataBound(e: kendo.ui.AutoCompleteDataBoundEvent): void {
        this.setBusy(false);

        if (this.controlDefinition.LoadDataAsynchronously) {
            this.setDataPromiseResolve();
        }
        this.trigger(ControlEventTypes.DataBound);
    }

    private onError(e: kendo.data.DataSourceErrorEvent): void {
        console.log(e.errorThrown, e.errors);
    }

    /**
     * Set the selected value.
     * @param value Value to be set.
     * @since 4.1.0
     */
    public setValue(value: string): void {
        let oldValue = this.getValue();
        if (oldValue !== value) {
            this._kendoElement.value(value);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Get the selected value.
     * @since 4.1.0
     */
    public getValue(): string {
        let val = this._kendoElement.value();
        return val == "" ? null : val;
    }

    /**
     * Get the kendo autocomplete control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/autocomplete|here} for the api definition.
     * @since 4.1.0
     */
    public getKendoElement(): kendo.ui.AutoComplete {
        return this._kendoElement;
    }

    /**
     * Occurs when control value was changed.
     * @param e EventArgs.
     */
    private onChange(e: kendo.ui.AutoCompleteChangeEvent) {
        // check if the user entered a text but didnt select a value
        // if (this._kendoElement.text() != "" && this._kendoElement.select() === -1) {
        //     setTimeout(() => {
        //         this._kendoElement.value(""); //reset widget
        //     }, 15);
        // } else {
        this.trigger(ControlEventTypes.Change, this);
        // }
    }

    /**
     * Enable the control.
     * @since 4.1.0
     */
    public enable(): void {
        this._kendoElement.enable(true);
    }

    /**
     * Disable the control.
     * @since 4.1.0
     */
    public disable(): void {
        this._kendoElement.enable(false);
    }

    /**
     * Returns true if the control is enabled.
     * @since 4.1.0
     */
    public isEnabled(): boolean {
        return !this._kendoElement.element.prop("disabled");
    }

    /**
     * Get info for requesting data.
     * @hidden
     */
    public getDataRequest(): DataRequestOption {
        if (this._kendoElement == null) {
            return null;
        }
        let result: DataRequestOption = {
            ControlID: this.controlDefinition.ControlID,
            SearchValue: [],
            Take: this.controlDefinition.LoadDataAsynchronously ? this.controlDefinition.ItemsPerRequest || 10 : null,
            Skip: null,
            FilterOperator: null,
            SearchText: null,
        };
        return result;
    }

    private setDataPromise: Promise<void>;
    private setDataPromiseResolve: any;

    /**
     * Set the odata-result to the control.
     * @param data Data to be set.
     * @hidden
     */
    public setData(data: IODataResult): Promise<void> {
        this.data = data;

        if (this.controlDefinition.LoadDataAsynchronously) {
            // the loading of the virtual combobox continues after returning above methods.
            // therefore we need to wait for the promise to be resolved in the onDataBound handler
            this.setDataPromise = new Promise((resolve, rject) => {
                this.setDataPromiseResolve = resolve;
            });

            this._kendoElement.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
            this.dataSource = this._kendoElement.dataSource;
            this._kendoElement.dataSource.read();

            return this.setDataPromise;
        } else {
            this._kendoElement.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
            this.dataSource = this._kendoElement.dataSource;
            this._kendoElement.dataSource.read();
            return Promise.resolve();
        }
    }

    /**
     * Show loading indicator on the control.
     * @param isBusy True if loading indicator should be shown.
     * @since 4.1.0
     */
    public setBusy(isBusy: boolean): void {
        if (isBusy) {
            (<any>this._kendoElement)._showBusy();
        } else {
            (<any>this._kendoElement)._hideBusy();
        }
    }

    /**
     * Returns true if an item with given value is in the dropdown list.
     * @param value The value to be searched.
     * @since 4.1.0
     * @returns True if the value is in the list of available values.
     * @example
     * ```JavaScript
     * @@ctr:1337@@.containsValue("1");
     * ```
     */
    public containsValue(value: string): boolean {
        this._kendoElement.dataSource.fetch(() => {
            let list = this._kendoElement.dataSource.data();
            let columnName = this.controlDefinition.CommandValueColumnName;
            for (let i = 0, len = list.length; i < len; i++) {
                if (list[i][columnName] == value) {
                    return true;
                }
            }
        });
        return false;
    }

    /**
     * Reload the control items with data from the server.
     * @since 4.1.0
     */
    public reload(): Promise<void> {
        if (this.controlDefinition.LoadDataAsynchronously) {
            (<any>this._kendoElement).listView._values = [""];
        }
        return this.execute<Promise<void>>(ControlEventTypes.ControlReloadRequested);
    }

    /**
     * Set the search filter for the combobox.
     * @param text Text to be searched.
     * @since 4.1.0
     */
    public setSearch(text: string): void {
        this._kendoElement.search(text);
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.1.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement) {
            this._kendoElement.destroy();
        }
        if (this.jQueryElement) {
            this.jQueryElement.off("focusout");
        }
    }
}
